import React from 'react';
import { Radio } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { relabelOptions, scenarioOptionLabel } from '../cardTypes';
import { AddButton, Field, isBlank, ItemActions, RichTextArea, Section } from '../fields';
import { asArray, moveItem, removeAt, replaceAt } from '../listOps';
import { CardEditorProps } from './types';

interface ScenarioOption {
  id: string;
  text: string;
  correct: boolean;
  [key: string]: unknown;
}

const CORRECT_OPTIONS = [
  { label: <><CheckOutlined /> Correct</>, value: true },
  { label: 'Wrong', value: false },
];

/**
 * Options and their explanations (stored separately, keyed by option id) edited side by side.
 * Moving, adding or deleting re-letters the ids, since the apps show an option's id as its letter.
 */
const ScenarioEditor: React.FC<CardEditorProps> = ({ card, onChange }) => {
  const options = asArray<ScenarioOption>(card.options);
  const explanations: Record<string, string> = card.explanations ?? {};

  const update = (nextOptions: ScenarioOption[], nextExplanations = explanations) =>
    onChange({ ...card, options: nextOptions, explanations: nextExplanations });
  const restructure = (nextOptions: ScenarioOption[]) => {
    const relabeled = relabelOptions(nextOptions, explanations);
    update(relabeled.options, relabeled.explanations);
  };
  const patchOption = (index: number, patch: Partial<ScenarioOption>) =>
    update(replaceAt(options, index, { ...options[index], ...patch }));

  return (
    <Section
      title="Answer options"
      help="Mark the right answer. The option's explanation is shown after the user picks it."
    >
      <div className="lbe-items">
        {options.map((option, i) => (
          <div key={i} className={`lbe-option${option.correct ? ' is-correct' : ''}`}>
            <div className="lbe-option-head">
              <span className="lbe-letter">{scenarioOptionLabel(option, i)}</span>
              <Radio.Group
                size="small"
                optionType="button"
                buttonStyle="solid"
                value={!!option.correct}
                options={CORRECT_OPTIONS}
                onChange={(e) => patchOption(i, { correct: e.target.value })}
              />
              <span style={{ flex: 1 }} />
              <ItemActions
                index={i}
                count={options.length}
                onMove={(to) => restructure(moveItem(options, i, to))}
                onRemove={() => restructure(removeAt(options, i))}
                confirmRemove={`Delete option ${scenarioOptionLabel(option, i)}?`}
              />
            </div>
            <Field label="Answer" required>
              <RichTextArea
                minRows={1}
                value={option.text}
                status={isBlank(option.text) ? 'warning' : undefined}
                onChange={(text) => patchOption(i, { text })}
              />
            </Field>
            <Field label="Explanation" required>
              <RichTextArea
                value={explanations[option.id]}
                status={isBlank(explanations[option.id]) ? 'warning' : undefined}
                onChange={(text) => update(options, { ...explanations, [option.id]: text })}
              />
            </Field>
          </div>
        ))}
        <AddButton onClick={() => restructure([...options, { id: '', text: '', correct: false }])}>Add option</AddButton>
      </div>
    </Section>
  );
};

export default ScenarioEditor;
